/* BVJ hero video v3: one controller; real gesture fallback; no reload/retry loop. */
if (!customElements.get('hero-video')) {
  class HeroVideo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      if (!this.querySelector('video')) {
        this.childObserver = new MutationObserver(() => {
          if (this.querySelector('video')) {
            this.childObserver.disconnect();
            this.connectedCallback();
          }
        });
        this.childObserver.observe(this, { childList: true, subtree: true });
        return;
      }
      this.ready = true;
      this.video = this.querySelector('video');
      this.hero = this.closest('.hero') || this.parentElement;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.listeners = [];
      this.timers = [];
      this.requestId = (this.requestId || 0) + 1;
      this.pending = false;
      this.blocked = false;
      this.userPaused = false;
      this.userStarted = false;
      this.fallbackUsed = false;
      this.dataset.videoController = 'bvj-v3';
      this.prepare();
      this.createControl();

      this.listen(this.control, 'click', (event) => {
        event.preventDefault();
        if (!this.video.paused && this.video.readyState >= 2) {
          this.userPaused = true;
          this.video.autoplay = false;
          this.stop();
          this.setState('paused');
        } else {
          if (this.video.error) this.video.load();
          // play() must run synchronously inside the trusted click/touch handler.
          this.play(true);
        }
      });
      ['loadedmetadata', 'loadeddata', 'canplay'].forEach((name) => {
        this.listen(this.video, name, () => this.play());
      });
      this.listen(this.video, 'playing', () => {
        if (!this.allowed()) { this.stop(); return; }
        this.blocked = false;
        delete this.dataset.videoError;
        this.setState('playing');
      });
      this.listen(this.video, 'pause', () => {
        this.setState(this.userPaused ? 'paused' : (this.blocked ? 'blocked' : 'idle'));
      });
      this.listen(this.video, 'error', () => this.onMediaError());
      this.listen(document, 'visibilitychange', () => this.resumeOrStop());
      this.listen(window, 'pageshow', () => this.resumeOrStop());
      this.listen(window, 'pagehide', () => this.stop());
      this.listen(window, 'online', () => {
        if (this.video.error) this.video.load();
        this.play();
      });
      // touchstart alone does not reliably grant playback permission on iOS.
      ['touchend', 'click', 'keydown'].forEach((name) => {
        this.listen(document, name, (event) => {
          if (!event.isTrusted || event.target.closest?.('.bvj-hero-video-control')) return;
          if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
          if (!this.motion.matches && !this.userPaused && this.video.paused) this.play(true);
        }, { passive: true });
      });
      const motionChanged = () => {
        this.userStarted = false;
        this.video.style.removeProperty('display');
        this.video.autoplay = !this.motion.matches && !this.userPaused;
        this.resumeOrStop();
      };
      if (this.motion.addEventListener) this.listen(this.motion, 'change', motionChanged);
      else {
        this.motion.addListener(motionChanged);
        this.listeners.push(() => this.motion.removeListener(motionChanged));
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(() => this.resumeOrStop(), { threshold: 0.01 });
        this.observer.observe(this);
      }
      this.setState(this.motion.matches ? 'reduced-motion' : 'loading');
      this.resumeOrStop();
      // Bounded readiness retries; never reload a video that is still buffering.
      [350, 1200, 3000].forEach((delay) => {
        this.timers.push(setTimeout(() => this.play(), delay));
      });
    }

    listen(target, name, handler, options) {
      target.addEventListener(name, handler, options);
      this.listeners.push(() => target.removeEventListener(name, handler, options));
    }

    prepare() {
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.loop = true;
      this.video.controls = false;
      this.video.preload = 'auto';
      this.video.autoplay = !this.motion.matches;
      this.video.setAttribute('muted', '');
      this.video.setAttribute('playsinline', '');
      this.video.setAttribute('webkit-playsinline', '');
    }

    createControl() {
      this.control = document.createElement('button');
      this.control.type = 'button';
      this.control.className = 'bvj-hero-video-control';
      if (this.video.id) this.control.setAttribute('aria-controls', this.video.id);
      this.control.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path></path></svg><span></span>';
      this.hero.appendChild(this.control);
    }

    visible() {
      const rect = this.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    }

    allowed() {
      return this.ready && this.isConnected && !document.hidden && this.visible() &&
        !this.userPaused && (!this.motion.matches || this.userStarted);
    }

    setState(state) {
      if (!this.ready) return;
      this.dataset.videoState = state;
      const playing = state === 'playing';
      const label = playing ? 'Video pauzeren' : (state === 'error' ? 'Video opnieuw proberen' : 'Video afspelen');
      this.control.setAttribute('aria-label', label);
      this.control.querySelector('span').textContent = label;
      this.control.querySelector('path').setAttribute('d', playing ? 'M6 4h4v16H6zM14 4h4v16h-4z' : 'M7 4v16l13-8z');
    }

    play(fromGesture = false) {
      if (!this.ready || !this.isConnected) return;
      if (fromGesture) {
        this.userStarted = true;
        this.userPaused = false;
        this.blocked = false;
        this.video.autoplay = true;
        // Reduced motion stays respected until the visitor explicitly opts in.
        if (this.motion.matches) this.video.style.setProperty('display', 'block', 'important');
      }
      if (!this.allowed() || (this.blocked && !fromGesture)) return;
      if (!this.video.paused && this.video.readyState >= 2) {
        this.setState('playing');
        return;
      }
      if (this.pending && !fromGesture) return;
      this.video.muted = true;
      this.video.defaultMuted = true;
      const id = ++this.requestId;
      this.pending = true;
      try {
        const result = this.video.play();
        Promise.resolve(result).then(() => {
          if (!this.ready || id !== this.requestId) return;
          this.pending = false;
          if (!this.allowed()) this.stop();
          else if (!this.video.paused) this.setState('playing');
        }).catch((error) => this.playFailed(error, id));
      } catch (error) {
        this.playFailed(error, id);
      }
    }

    playFailed(error, id) {
      if (!this.ready || id !== this.requestId) return;
      this.pending = false;
      this.dataset.videoError = error?.name || 'PlaybackError';
      if (error?.name === 'NotAllowedError') {
        this.blocked = true;
        this.setState('blocked');
      } else if (error?.name !== 'AbortError') {
        this.setState('error');
      }
    }

    onMediaError() {
      this.dataset.videoError = 'MediaError:' + (this.video.error?.code || 0);
      const fallback = this.video.dataset.bvjFallbackSrc;
      if (!this.fallbackUsed && fallback && new URL(fallback, document.baseURI).href !== this.video.currentSrc) {
        this.fallbackUsed = true;
        this.pending = false;
        this.requestId++;
        this.video.src = fallback;
        this.video.load();
        this.play();
      } else {
        this.setState('error');
      }
    }

    stop() {
      this.requestId++;
      this.pending = false;
      this.video.pause();
    }

    resumeOrStop() {
      if (this.allowed()) this.play();
      else {
        this.stop();
        this.setState(this.motion.matches && !this.userStarted ? 'reduced-motion' : 'paused');
      }
    }

    disconnectedCallback() {
      this.childObserver?.disconnect();
      this.observer?.disconnect();
      (this.listeners || []).forEach((remove) => remove());
      (this.timers || []).forEach(clearTimeout);
      if (this.video) this.stop();
      this.control?.remove();
      this.ready = false;
    }
  }
  customElements.define('hero-video', HeroVideo);
}
